import { logAmplitudeEvent } from 'amplitude/amplitude'
import { addTemplateError, setTemplateResult, updateTemplateResult } from 'ducks/autoDesign'
import isEqual from 'lodash/isEqual'
import {
  AUTO_ADD_STARRED_KEY,
  AUTO_DESIGN_MSG_ALL_FACETS_FILTERED,
  AUTO_DESIGN_MSG_CREATE_SITE_MODEL,
  AUTO_DESIGN_MSG_OUTSIDE_ORIGINAL_PARCEL,
  AUTO_DESIGN_MSG_ROOF_FACET_CHANGED,
  AUTO_DESIGN_USED_KEY,
  PINNED_TEMPLATES_KEY,
} from './constants'
import { touchAutoDesignLastActivity } from './hooks/useHasUsedAutoDesignInLast30Days'
import { SiteModelStatus, SystemTemplateFull } from './types'

const buildTemplateSummary = (opts: {
  kwStc: number
  batterykWh: number
  autoPopulatePanels?: boolean
}): string => {
  const translate = window.translate
  const pvPart = opts.autoPopulatePanels === false ? translate('Manual panels') : `${opts.kwStc}kW PV`
  const batteryPart = opts.batterykWh > 0 ? `${opts.batterykWh}kWh ${translate('Battery')}` : translate('No Battery')
  return `${pvPart} / ${batteryPart}`
}

export const templateDependsOnUsage = (template: SystemTemplateFull) => {
  const constraints = template?.constraints
  if (!Array.isArray(constraints)) return false
  return constraints.some((t) => t?.type === 'consumption_offset_percentage')
}

export function getItemsChangedForDirtyDesigns(siteModelParamsRef: any, currentParamsRef: any): string[] {
  const changes: string[] = []
  if (!isEqual(siteModelParamsRef.current.usage ?? null, currentParamsRef.current.usage ?? null)) {
    changes.push('usage')
  }
  return changes
}

export const usageToAnnualKwh = (usage) => {
  try {
    return JSON.parse(usage).normalized.annual
  } catch (e) {
    console.error(e)
    return NaN
  }
}

export const runAutoDesignForTemplate = (
  template: any,
  facetsGeoJson,
  autoAddOnSuccess: boolean,
  siteModelParamsRef,
  autoLoadTemplateIdsRef,
  unsavedProjectData,
  dispatch?: any,
  projectId?: number,
  options?: { dispatchStart?: boolean; designDataMapRef?: React.MutableRefObject<Map<string, any>> }
) => {
  const runId = crypto.randomUUID()

  // Update Redux state to track this template is starting
  if (options?.dispatchStart !== false && dispatch && projectId) {
    dispatch(
      setTemplateResult(projectId, {
        template: { id: template.id, name: template.name },
        summary: '',
        status: 'designing-auto',
        messages: [],
        runId,
        timestamp: Date.now(),
      })
    )
  }

  window.AutoDesignHelper.autoDesignRunAndLoadWorker(
    null,
    [template],
    null,
    facetsGeoJson,
    siteModelParamsRef.current.lonlat,
    null,
    false,
    null,
    null,
    null,
    false,
    false,
    { unsavedProjectData: unsavedProjectData }
  )
    .then((response) => {
      // Handle the case where response might be null (all templates failed)
      if (!response) {
        throw new Error('Auto-design failed - no response received')
      }

      // Extract messages and failure info from the auto-design result
      let messages: string[] = []
      let valid = false
      let kwStc = 0
      let batterykWh = 0

      // Check if we have designData (success case)
      if (response.designData?.object?.userData?.autoDesignMessages) {
        messages = response.designData.object.userData.autoDesignMessages || []

        // check if system exists and is valid in design data
        // @TODO: Improve checks
        valid = response.designData.object?.children?.filter((c) => c.type === 'OsSystem').length === 1
        kwStc = response.designData.object?.children?.filter((c) => c.type === 'OsSystem')[0]?.userData?.kw_stc || 0
        batterykWh =
          response.designData.object?.children?.filter((c) => c.type === 'OsSystem')[0]?.userData?.battery_total_kwh ||
          0
      } else {
        // Handle case where designData is null but we have autoDesignResult
        // This would be the case when all templates fail
        valid = false

        // Extract messages from autoDesignResult
        if (response.autoDesignResult?.messages) {
          messages = response.autoDesignResult.messages.map((msg: any) => msg.message)
        } else {
          messages = ['Auto-design failed - no valid system could be created']
        }
      }

      // Save design data out-of-redux in the provided map if present
      const designDataKey = `${projectId}:${template.id}:${runId}`
      if (response?.designData && options?.designDataMapRef) {
        options.designDataMapRef.current.set(designDataKey, response.designData)
      }

      // Update Redux state with result (without heavy designData)
      if (dispatch && projectId) {
        // Extract failure reason from autoDesignResult if available
        let failureReason = ''
        if (!valid && response.autoDesignResult?.template_results) {
          const templateResult = response.autoDesignResult.template_results.find(
            (tr: any) => tr.template_id === template.id
          )
          if (templateResult?.failure_reason) {
            failureReason = templateResult.failure_reason
          }
        }

        dispatch(
          setTemplateResult(projectId, {
            template: { id: template.id, name: template.name },
            status: valid ? 'success' : 'error',
            summary: valid
              ? buildTemplateSummary({
                  kwStc,
                  batterykWh,
                  autoPopulatePanels: template?.auto_populate_panels,
                })
              : '',
            messages: messages,
            failureReason: failureReason || (valid ? '' : 'Auto-design failed for this template'),
            runId,
            timestamp: Date.now(),
            designDataRefId: designDataKey,
          } as any)
        )

        // Also add a template-specific error if the template failed
        if (!valid && failureReason) {
          dispatch(
            addTemplateError(projectId, template.id, {
              errorCode: 'AUTO_DESIGN_TEMPLATE_FAILED',
              message: failureReason,
              details: messages,
              runId,
            })
          )
        }
      }

      // Check if we should auto-load this result when it's ready
      let systemAdded = false

      if (autoLoadTemplateIdsRef.current.has(template.id.toString()) && valid) {
        // Remove from auto-load set
        autoLoadTemplateIdsRef.current.delete(template.id.toString())

        // Call handleAddSystem for auto-load cases
        const designData = options?.designDataMapRef?.current.get(designDataKey) || response.designData
        handleAddSystem(designData, template.id, dispatch, projectId)
        systemAdded = true
      }

      if (autoAddOnSuccess && valid && !systemAdded) {
        // Call handleAddSystem for autoAddOnSuccess cases (only if not already called)
        const designData = options?.designDataMapRef?.current.get(designDataKey) || response.designData
        handleAddSystem(designData, template.id, dispatch, projectId)
      }
    })
    .catch((error) => {
      // Update Redux state with error result
      if (dispatch && projectId) {
        dispatch(
          setTemplateResult(projectId, {
            template: { id: template.id, name: template.name },
            status: 'error',
            summary: '',
            failureReason: error.message || 'Auto-design failed',
            messages: [error.message || 'Auto-design failed'],
            runId,
            timestamp: Date.now(),
          })
        )

        // Also add a template-specific error
        dispatch(
          addTemplateError(projectId, template.id, {
            errorCode: 'AUTO_DESIGN_TEMPLATE_FAILED',
            message: error.message || 'Auto-design failed',
            details: error.stack || error.toString(),
            runId,
          })
        )
      }
    })
}

export const findExistingSystemInScene = (designData: any): string | null => {
  if (!designData?.object?.children) {
    return null
  }

  const systemsInDesignData = designData.object.children.filter((child: any) => child.type === 'OsSystem')

  for (const systemData of systemsInDesignData) {
    const systemUuid = systemData.uuid || systemData.userData?.uuid
    if (!systemUuid) continue

    if (window.editor.objectByUuid(systemUuid) !== undefined) {
      return systemUuid
    }
  }

  return null
}

export const handleAddSystem = (
  designData: any,
  templateId: string,
  dispatch?: any,
  projectId?: number,
  onComplete?: (templateId: string) => void
) => {
  if (designData?.object?.userData) {
    designData.object.userData.autoDesignNotificationsShown = true
  }

  const addResult = window.SceneHelper.handleAutoDesignDesignData(designData, {
    action: 'add',
    authoredBy: `system-template:${templateId}`,
  })

  if (addResult && typeof addResult.then === 'function') {
    // It's a Promise - only update Redux state when Promise resolves
    addResult
      .then((response: any) => {
        const systemUuid = response?.systems?.[0]?.uuid

        // Update Redux state only when Promise resolves
        if (dispatch && projectId) {
          dispatch(
            updateTemplateResult(projectId, parseInt(templateId), {
              status: 'added',
              systemUuid: systemUuid,
              addedTimestamp: Date.now(),
            })
          )
        }

        window.SceneHelper.replaceFirstSystemWithAutoDesign()

        touchAutoDesignLastActivity()

        // Call completion callback
        onComplete?.(templateId)
      })
      .catch((error) => {
        console.error('Failed to add system:', error)

        // Update Redux state on error
        if (dispatch && projectId) {
          dispatch(
            updateTemplateResult(projectId, parseInt(templateId), {
              status: 'error',
            })
          )
        }

        // Call completion callback even on error
        onComplete?.(templateId)
      })
  } else {
    // It's not a Promise - update Redux state immediately
    const systemUuid = (addResult as any)?.systems?.[0]?.uuid

    if (dispatch && projectId) {
      dispatch(
        updateTemplateResult(projectId, parseInt(templateId), {
          status: 'added',
          systemUuid: systemUuid,
          addedTimestamp: Date.now(),
        })
      )
    }

    window.SceneHelper.replaceFirstSystemWithAutoDesign()

    touchAutoDesignLastActivity()

    // Call completion callback
    onComplete?.(templateId)
  }

  logAmplitudeEvent('auto_design_system_added', {
    source: 'System Added',
    context: 'auto_design_side_drawer',
    templateId: templateId,
    projectId: projectId ?? 'unknown',
  })
}

export const userHasUsedAutoDesign = (): boolean => {
  try {
    const storedValue = localStorage.getItem(AUTO_DESIGN_USED_KEY)
    const autoDesignUsed = storedValue ? JSON.parse(storedValue) : false
    return autoDesignUsed
  } catch {
    return true
  }
}

export const setUserHasUsedAutoDesign = (hasUsed: boolean) => {
  try {
    localStorage.setItem(AUTO_DESIGN_USED_KEY, JSON.stringify(hasUsed))
  } catch {
    // Silently fail if localStorage is not available
  }
}

export const getPinnedTemplateIds = (availableTemplates?: Array<any>): Array<string> => {
  try {
    const pinned = localStorage.getItem(PINNED_TEMPLATES_KEY)
    const allPinnedIds = pinned ? JSON.parse(pinned) : []

    if (!availableTemplates || availableTemplates.length === 0) {
      return allPinnedIds
    }

    const availableTemplateIds = availableTemplates.map((template) => template.id.toString())
    const filteredPinnedIds = allPinnedIds.filter((id) => availableTemplateIds.includes(id))

    return filteredPinnedIds
  } catch {
    return []
  }
}

export const setPinnedTemplateIdsToStorage = (ids: Array<string>) => {
  try {
    localStorage.setItem(PINNED_TEMPLATES_KEY, JSON.stringify(Array.from(ids)))
  } catch {
    // Silently fail if localStorage is not available
  }
}

export const getAutoAddStarredSetting = (): boolean => {
  try {
    const setting = localStorage.getItem(AUTO_ADD_STARRED_KEY)
    return setting ? JSON.parse(setting) : false
  } catch {
    return false
  }
}

export const setAutoAddStarredSetting = (enabled: boolean) => {
  try {
    localStorage.setItem(AUTO_ADD_STARRED_KEY, JSON.stringify(enabled))
  } catch {
    // Silently fail if localStorage is not available
  }
}

const getSpatialFacets = () => {
  return window.editor?.enumerateFacets?.({ includeNonSpatialFacets: false }) || []
}

export const detectFlatFacetsIssue = (autoDesignResults: any[]): 'allFacetsFiltered' | null => {
  const spatialFacets = getSpatialFacets()
  if (spatialFacets.length > 0) {
    return null
  }

  for (const result of autoDesignResults) {
    // Check if all facets were filtered out due to a flat roof
    // Only check failure reasons for completed results, not for templates still designing
    if (result.failureReason && typeof result.failureReason === 'string' && !result.status.includes('designing')) {
      if (result.failureReason.includes('Flat roofs are currently not supported by Ada Auto Design')) {
        return 'allFacetsFiltered'
      }
    }
  }
  return null
}

export const addFlatFacetsToRefreshItems = (
  itemsChangedForRefresh: string[],
  flatFacetsIssue: 'allFacetsFiltered' | null
): string[] => {
  const items = [...itemsChangedForRefresh]
  if (flatFacetsIssue) {
    items.push(flatFacetsIssue)
  }
  return items
}

export const getChangeTypeFromHeading = (heading: string): 'facets' | 'location' | 'other' => {
  const lowerHeading = heading.toLowerCase()

  if (lowerHeading.includes('facets') || lowerHeading.includes('roof')) {
    return 'facets'
  }
  if (lowerHeading.includes('location') || lowerHeading.includes('address')) {
    return 'location'
  }

  return 'other'
}

export const getSimplifiedTooltipMessage = (refreshMessages: Array<{ heading: string; message: string }>): string => {
  if (refreshMessages.length === 0) {
    return 'We have detected changes. Please regenerate the site model before adding this design.'
  }

  const firstMessage = refreshMessages[0]
  const changeType = getChangeTypeFromHeading(firstMessage.heading)

  switch (changeType) {
    case 'facets':
      return 'Roof facets have changed. Please regenerate the site model to include them in your auto design.'
    case 'location':
      return 'We have detected a location change. Please regenerate the site model to ensure the correct roof is detected.'
    default:
      return 'We have detected changes. Please regenerate the site model before adding this design.'
  }
}

export const buildRefreshMessages = (
  itemsChangedForRefresh: string[],
  opts: {
    lowQualityImageryDetectedAndNoFacets?: boolean
    lowQualityImageryDetected?: boolean
    siteModelStatus?: SiteModelStatus
  }
): Array<{ heading: string; message: string }> => {
  const messages: Array<{ heading: string; message: string }> = []

  if (itemsChangedForRefresh.includes('facets')) {
    const isLowQualityFirstFacets =
      opts.lowQualityImageryDetectedAndNoFacets === false &&
      opts.lowQualityImageryDetected === true &&
      opts.siteModelStatus !== 'ready'

    if (isLowQualityFirstFacets) {
      messages.push({
        heading: 'Site Model Required',
        message: AUTO_DESIGN_MSG_CREATE_SITE_MODEL,
      })
    } else {
      messages.push({
        heading: 'Roof facet changes detected',
        message: AUTO_DESIGN_MSG_ROOF_FACET_CHANGED,
      })
    }
  }

  // Prioritize "outside original parcel" message - it takes precedence over all other messages so we don't get flat roof messages blocking you from fixing your site model
  if (itemsChangedForRefresh.includes('location')) {
    messages.push({
      heading: 'Location Changed',
      message: AUTO_DESIGN_MSG_OUTSIDE_ORIGINAL_PARCEL,
    })
    return messages // Return early, don't show other messages
  }

  if (itemsChangedForRefresh.includes('allFacetsFiltered')) {
    messages.push({
      heading: 'Flat Roof Detected',
      message: AUTO_DESIGN_MSG_ALL_FACETS_FILTERED,
    })
  }

  return messages
}
